<script>
  import { page } from '$app/stores';
  import { env as envPublic } from '$env/dynamic/public';
  import { Navbar, NavBrand, NavLi, NavUl } from 'flowbite-svelte';
  import SearchInput from '$components/locations/+searchInput.svelte';

  /** @type {string} */
  export let logo;

  /** @type {string} */
  export let logoId = 'logo';

  /** @type {string} */
  export let logoRedirectTo = '/';

  /** @type {string}*/
  export let navbarClassName = '';

  /** @type {boolean}*/
  export let showLogin = true;

  /** @type {boolean}*/
  export let showLoginMobile = false;

  /** @type {boolean}*/
  export let showBalance = true;

  /** @type {boolean}*/
  export let showProfileLink = true;

  /** @type {boolean} */
  export let isBurgerVisible = true;

  /** @type {boolean | null | undefined} */
  export let isEmbedded = false;

  export let searchParam = 'filter[s]';

  /**
   * @typedef {Object} Item
   * @property {string} label - The display text of the item.
   * @property {string} href - The URL of the item.
   * @property {'_self' | '_blank' | string | undefined} [target] - Where the link should open.
   * @property {'noopener' | 'noreferrer' | 'nofollow' | 'external' | string| undefined} [rel] - The `rel` attribute for links.
   * @property {'highlight' | string | undefined} [highlight] - Highlight option, if applicable.
   */

  /**
   * @type {Item[]} items
   */
  export let items;

  /**
   * @type {Item[] | null | undefined} [additional_items] - Optional property, can be undefined,adds items with divider before the main items.
   */
  export let additional_items = undefined;

  export let redirectUrl = '';

  /** @type {boolean} */
  let menuOpen = false;

  /** @type {boolean} */
  export let loggedIn;

  /** @type {boolean} */
  export let searchShown;

  /** @type {boolean} */
  export let mobileSearchShown = true;

  /**
   * @typedef {Object} ProfileLink
   * @property {string} label - The display text of the link.
   * @property {string} href - The URL of the link.
   */

  /**
   * @typedef {Object} ProfileLinks
   * @property {ProfileLink} first - The primary link (e.g., "Log ind").
   * @property {ProfileLink} second - The secondary link (e.g., "Tilmeld dig").
   * @property {ProfileLink} [mobile] - An optional link for mobile (e.g., "Log ind" or "Tilmeld dig" for mobile).
   */

  /**
   * Links for the profile when the user is not logged in.
   * @type {ProfileLinks}
   */
  export let profileLinks = {
    first: { href: '', label: '' },
    second: { href: '', label: '' },
    mobile: { href: '', label: '' },
  };

  /**
   * @typedef {Object} Balance
   * @property {number} value - The numeric value of the balance.
   */

  /**
   * URL of the user's avatar image.
   * @type {string}
   */
  export let currencyText = 'kr.';

  /**
   * @type {Balance}
   */
  export let balance = { value: 0 };

  const toggleMenu = () => {
    menuOpen = !menuOpen;
  };

  const handleSearch = () => {
    menuOpen = false; // Close the menu when search is performed
  };
</script>

<NavBrand href={logoRedirectTo} id={logoId}>
  <img src={logo} alt={envPublic.PUBLIC_APP_NAME} />
</NavBrand>
{#if showLoginMobile}
  {#if loggedIn}
    <div class="mobile-profile">
      <div class="balance-nav"><span class="value">{balance?.value?.toFixed(2) || 0} {currencyText}</span></div>
    </div>
  {:else}
    <div class="mobile-signup signup">
      <a href={profileLinks.mobile?.href}>{profileLinks.mobile?.label} </a>
    </div>
  {/if}
{/if}
<Navbar class={`top-nav-menu ${menuOpen ? 'menu-open' : 'menu-closed'} ${navbarClassName}`}>
  {#if isBurgerVisible}
    <div class="hamburger">
      <button class="open-menu" on:click={toggleMenu}>
        <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M5 7h14M5 12h14M5 17h14" />
        </svg>
      </button>
      <button class="close-menu" on:click={() => (menuOpen = !menuOpen)}>
        <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18 17.94 6M18 18 6.06 6" />
        </svg>
      </button>
    </div>
  {/if}
  <NavUl class="nav-links">
    <!-- Display whatever is passed to items -->
    {#each items as item}
      <NavLi href={item.href} target={item.target ?? '_self'} rel={item.rel} class="{item.highlight ? 'highlight' : ''} {$page.url.pathname.includes(item.href) ? 'active' : ''}">{item.label}</NavLi>
    {/each}
  </NavUl>
</Navbar>

{#if searchShown}
  <SearchInput {searchParam} {redirectUrl} liveSearchEnabled={false} {isEmbedded} />
{/if}

<div class="mobile-menu">
  <div class="wrap">
    {#if loggedIn && showBalance}
      <div class="balance-nav">Saldo: <span class="value">{balance?.value?.toFixed(2) || 0} kr.</span></div>
    {/if}

    <!-- Show search input in burger menu -->
    {#if mobileSearchShown}
      <SearchInput {searchParam} {redirectUrl} liveSearchEnabled={false} on:search={handleSearch} />
    {/if}

    {#if loggedIn && showProfileLink}
      <div class="profile">
        <a href="/min-konto" on:click={toggleMenu}>Profil</a>
      </div>
    {/if}

    <NavUl class="nav-links">
      <!-- Only on main nav and only when these additional items are passed -->
      {#if additional_items && additional_items.length > 0}
        {#each additional_items as item}
          <NavLi href={item.href} target={item.target ?? '_self'} rel={item.rel} class="{item.highlight ? 'highlight' : ''} {$page.url.pathname.includes(item.href) ? 'active' : ''}">{item.label}</NavLi>
          <!-- if last element add <div class="nav-links-divider"> only within this map-->
          {#if item === additional_items[additional_items.length - 1]}
            <div class="nav-links-divider"></div>
          {/if}
        {/each}
      {/if}
      {#each items as item}
        <NavLi href={item.href} target={item.target ?? '_self'} rel={item.rel} class={item.highlight ? 'highlight' : ''} on:click={toggleMenu}>{item.label}</NavLi>
      {/each}
    </NavUl>
    {#if showLogin}
      {#if loggedIn}
        <div class="profile-second">
          <a href="/min-konto" on:click={toggleMenu}>Profil</a>
        </div>
      {:else}
        <div class="login">
          <a href={profileLinks.first.href} on:click={toggleMenu}>{profileLinks.first.label} </a>
          <a href={profileLinks.second.href} on:click={toggleMenu}>{profileLinks.second.label}</a>
        </div>
      {/if}
    {/if}
  </div>
</div>
